import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react'

import {
  RESUME_DETAIL_EDITOR_MIN_PX,
  RESUME_DETAIL_EDITOR_WIDTH_STORAGE_KEY,
  RESUME_DETAIL_PREVIEW_MIN_PX,
} from './resumeDetailSplitLayout'

function readStoredEditorWidthPx(): number | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(RESUME_DETAIL_EDITOR_WIDTH_STORAGE_KEY)
    if (!raw) return null
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) && n >= RESUME_DETAIL_EDITOR_MIN_PX ? n : null
  } catch {
    return null
  }
}

function clampEditorWidth(splitWidthPx: number, desiredPx: number): number {
  const max = Math.max(
    RESUME_DETAIL_EDITOR_MIN_PX,
    splitWidthPx - RESUME_DETAIL_PREVIEW_MIN_PX
  )
  return Math.min(Math.max(desiredPx, RESUME_DETAIL_EDITOR_MIN_PX), max)
}

export function useResumeDetailEditorResize(
  splitRef: RefObject<HTMLDivElement | null>,
  disabled = false
) {
  const [editorWidthPx, setEditorWidthPx] = useState<number | null>(readStoredEditorWidthPx)
  const [isResizing, setIsResizing] = useState(false)
  const dragRef = useRef(false)

  const editorPanelStyle: CSSProperties | undefined =
    editorWidthPx != null
      ? { width: editorWidthPx, flex: '0 0 auto' }
      : undefined

  const onResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (disabled) return
      event.preventDefault()
      const split = splitRef.current
      if (!split) return

      dragRef.current = true
      setIsResizing(true)

      const rect = split.getBoundingClientRect()
      const startWidth =
        editorWidthPx ??
        (leftPanelWidthFromDom(split) ?? Math.round(rect.width * 0.36))

      const startX = event.clientX

      const onMove = (moveEvent: globalThis.PointerEvent) => {
        if (!dragRef.current) return
        const delta = moveEvent.clientX - startX
        const next = clampEditorWidth(rect.width, startWidth + delta)
        setEditorWidthPx(next)
      }

      const onUp = () => {
        dragRef.current = false
        setIsResizing(false)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    [disabled, editorWidthPx, splitRef]
  )

  useEffect(() => {
    if (editorWidthPx == null) return
    try {
      window.localStorage.setItem(
        RESUME_DETAIL_EDITOR_WIDTH_STORAGE_KEY,
        String(editorWidthPx)
      )
    } catch {
      /* ignore quota */
    }
  }, [editorWidthPx])

  useEffect(() => {
    if (disabled || editorWidthPx == null) return
    const split = splitRef.current
    if (!split) return

    const ro = new ResizeObserver(() => {
      const w = split.clientWidth
      if (w <= 0) return
      setEditorWidthPx((prev) => {
        if (prev == null) return prev
        const clamped = clampEditorWidth(w, prev)
        return clamped === prev ? prev : clamped
      })
    })
    ro.observe(split)
    return () => ro.disconnect()
  }, [disabled, editorWidthPx, splitRef])

  return {
    editorWidthPx,
    editorPanelStyle,
    isResizing,
    onResizePointerDown,
    isResized: editorWidthPx != null,
  }
}

function leftPanelWidthFromDom(split: HTMLElement): number | null {
  const panel = split.querySelector('.resume-detail-editor-panel')
  if (!panel || !(panel instanceof HTMLElement)) return null
  const w = panel.getBoundingClientRect().width
  return w > 0 ? Math.round(w) : null
}
